// ST-1258 -Claude- Search results page with live narrowing and recommendation buckets.
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { searchProducts, searchSuggestions } from '@nearrme/api-client';
import { ProductTile, EmptyState, LoadingSkeleton, SearchBar, Card } from '@nearrme/ui-web';
import { getApiClient } from '../api/client';
import { LocationInput } from '../components/LocationInput';
import { useLocation } from '../hooks/useLocation';
import type { ConsumerProductSummary } from '@nearrme/types';

export function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { location } = useLocation();
  const q = params.get('q') ?? '';
  const [searchTerm, setSearchTerm] = useState(q);
  const lat = params.get('lat') ? Number(params.get('lat')) : location?.lat;
  const lng = params.get('lng') ? Number(params.get('lng')) : location?.lng;
  const activeQuery = searchTerm.trim();

  useEffect(() => {
    setSearchTerm(q);
  }, [q]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchTerm.trim();
      if (next === q) return;
      const nextParams = new URLSearchParams(params);
      if (next) nextParams.set('q', next);
      else nextParams.delete('q');
      navigate(`/search?${nextParams.toString()}`, { replace: true });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [navigate, params, q, searchTerm]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['search', activeQuery, lat, lng],
    queryFn: () => searchProducts(getApiClient(), { q: activeQuery, lat, lng, page: 1, pageSize: 20 }),
    enabled: activeQuery.length >= 2,
  });
  const { data: suggestionResponse, isFetching: suggestionsLoading } = useQuery({
    queryKey: ['search-suggestions', activeQuery],
    queryFn: () => searchSuggestions(getApiClient(), activeQuery, 8),
    enabled: activeQuery.length >= 2,
    staleTime: 60 * 1000,
  });

  const result = data?.data;
  const exactItems = result?.items ?? [];
  const relatedItems = result?.relatedItems ?? [];
  const affiliateItems = result?.affiliateItems ?? [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
        {activeQuery ? `${t('nav.search')}: "${activeQuery}"` : t('nav.search')}
      </h1>

      <div className="mb-4 max-w-3xl">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onSubmit={(next) => setSearchTerm(next)}
          placeholder="Search products, brands, SKU, warranty, or store need"
          suggestions={suggestionResponse?.data ?? []}
          suggestionsLoading={suggestionsLoading}
          onSuggestionSelect={(suggestion) => setSearchTerm(suggestion.query)}
        />
      </div>

      <div className="mb-6 grid gap-3 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <LocationInput compact />
        <Card padded className="border border-slate-200 dark:border-slate-800" data-testid="search-quality">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Search quality check</div>
          <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
            {result?.searchMessage ?? (activeQuery.length < 2 ? 'Type at least 2 characters to search.' : 'Searching verified products.')}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
            <Metric label="Matches" value={exactItems.length} />
            <Metric label="Related" value={relatedItems.length} />
            <Metric label="Affiliate" value={affiliateItems.length} />
          </div>
        </Card>
      </div>

      {isLoading && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <LoadingSkeleton key={i} className="h-64 w-full" />
          ))}
        </div>
      )}

      {error && (
        <EmptyState
          title={t('errors.generic')}
          description="We couldn't load results. Showing cached data if available."
        />
      )}

      {exactItems.length > 0 ? (
        <>
          <div className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {result?.totalCount} direct {result?.totalCount === 1 ? 'match' : 'matches'}
          </div>
          <section data-testid="direct-results">
            <ProductGrid products={exactItems} onOpen={(product) => navigate(`/p/${product.productId}`)} />
          </section>
        </>
      ) : (
        !isLoading && (
          <EmptyState
            title={t('search.noResults')}
            description={activeQuery.length < 2 ? 'Type at least 2 characters' : 'No direct match. Check related and affiliate opportunities below.'}
          />
        )
      )}

      {relatedItems.length > 0 && (
        <section className="mt-10" data-testid="related-results">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-slate-950 dark:text-slate-50">Related verified products</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              These are not exact matches, but they share brand, SKU text, or product intent with your search.
            </p>
          </div>
          <ProductGrid products={relatedItems} onOpen={(product) => navigate(`/p/${product.productId}`)} />
        </section>
      )}

      {affiliateItems.length > 0 && (
        <section className="mt-10" data-testid="affiliate-results">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-slate-950 dark:text-slate-50">Manufacturer-direct and affiliate opportunities</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              If local inventory is thin, Nearrme can surface manufacturer-direct or affiliate-backed offers without sending the buyer to an unverified marketplace.
            </p>
          </div>
          <ProductGrid products={affiliateItems} onOpen={(product) => navigate(`/p/${product.productId}`)} />
        </section>
      )}
    </div>
  );
}

function ProductGrid({ products, onOpen }: { products: ConsumerProductSummary[]; onOpen: (product: ConsumerProductSummary) => void }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {products.map((product) => (
        <ProductTile
          key={product.productId}
          product={product}
          onClick={onOpen}
        />
      ))}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-slate-100 px-2 py-2 dark:bg-slate-900">
      <div className="text-lg font-bold text-slate-950 dark:text-slate-50">{value}</div>
      <div className="text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );
}
